import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Container, Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCrown,
  FaGlasses,
  FaTshirt,
  FaShoppingBag,
  FaMagic,
  FaDownload,
  FaTimes,
  FaPlus,
  FaRegCopy,
  FaCheck,
  FaHistory
} from "react-icons/fa";
import {
  GiSparkles,
  GiEarrings,
  GiNecklace,
  GiSkirt,
  GiHighHeel,
  GiSocks,
  GiMirrorMirror,
  GiRibbon
} from "react-icons/gi";
import { IoShirtOutline, IoSparkles } from "react-icons/io5";
import "../css/Wafit.css";

// 기본 모델 이미지 (필수 항목 기본값)
const DEFAULT_MODEL_IMAGE = "https://www.wajok.com/attachments/image_1.png";

// 슬롯 정의 (부티크 쇼룸 플로팅 선반 대응)
const SLOT_CONFIGS = {
  // 좌측 선반 슬롯 (상체 및 페이스 악세사리)
  image_2: { key: "image_2", label: "머리핀/모자", enLabel: "HEADWEAR", icon: <FaCrown />, category: "acc", desc: "헤어핀, 머리띠, 모자 등" },
  image_3: { key: "image_3", label: "선글라스/안경", enLabel: "GLASSES", icon: <FaGlasses />, category: "acc", desc: "선글라스, 뿔테, 틴트안경 등" },
  image_4: { key: "image_4", label: "귀걸이/이어링", enLabel: "EARRINGS", icon: <GiEarrings />, category: "acc", desc: "귀걸이, 이어커프 등" },
  image_5: { key: "image_5", label: "목걸이/초커", enLabel: "NECKLACES", icon: <GiNecklace />, category: "acc", desc: "목걸이, 초커, 팬던트 등" },
  image_6: { key: "image_6", label: "상의 (Top)", enLabel: "TOPS", icon: <FaTshirt />, category: "clothes", desc: "블라우스, 티셔츠, 니트 등" },

  // 우측 선반 슬롯 (가방 및 하체/신발)
  image_7: { key: "image_7", label: "가방/핸드백", enLabel: "BAGS", icon: <FaShoppingBag />, category: "bag_shoes", desc: "핸드백, 숄더백, 미니백 등" },
  image_8: { key: "image_8", label: "하의 (Bottom)", enLabel: "BOTTOMS", icon: <GiSkirt />, category: "clothes", desc: "스커트, 슬랙스, 데님 등" },
  image_9: { key: "image_9", label: "스타킹/양말", enLabel: "HOSIERY", icon: <GiSocks />, category: "bag_shoes", desc: "스타킹, 니삭스, 양말 등" },
  image_10: { key: "image_10", label: "신발/슈즈", enLabel: "FOOTWEAR", icon: <GiHighHeel />, category: "bag_shoes", desc: "힐, 스니커즈, 로퍼 등" },

  // 인물 모델 (중앙 아치 거울)
  image_1: { key: "image_1", label: "피팅 모델", enLabel: "MODEL", icon: <GiMirrorMirror />, category: "model", desc: "전신 모델 이미지 (필수)" }
};

// 좌측 / 우측 컬럼 배치용 슬롯 키
const LEFT_SLOT_KEYS = ["image_2", "image_3", "image_4", "image_5", "image_6"];
const RIGHT_SLOT_KEYS = ["image_7", "image_8", "image_9", "image_10"];

// 로딩 중 노출할 팁 메시지들
const LOADING_TIPS = [
  "✨ AI 스타일리스트가 의류와 악세사리를 정교하게 피팅하고 있어요!",
  "💄 화장대 조명을 맞추고 조화로운 핏을 완성하는 중이에요.",
  "👗 착용 부위가 많을수록 더 입체적인 피팅 결과가 탄생해요.",
  "🎀 완성된 코디는 좌측 마이 룩북에 보관되고 다운로드할 수 있어요.",
  "✨ 최대 5~10분 정도 정성스럽게 렌더링되니 잠시만 기다려주세요 💕"
];

export default function Wafit() {
  // 1. 착용 슬롯 상태 (image_1 은 기본 모델 이미지)
  const [slots, setSlots] = useState(() => {
    const saved = localStorage.getItem("wafit_equipped_slots");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          image_1: parsed.image_1 || DEFAULT_MODEL_IMAGE,
          ...parsed
        };
      } catch (e) {
        console.error("Failed to parse saved slots", e);
      }
    }
    return {
      image_1: DEFAULT_MODEL_IMAGE,
      image_2: null,
      image_3: null,
      image_4: null,
      image_5: null,
      image_6: null,
      image_7: null,
      image_8: null,
      image_9: null,
      image_10: null
    };
  });

  // 2. 원피스 유무 (is_onepiece)
  const [isOnepiece, setIsOnepiece] = useState(() => {
    return localStorage.getItem("wafit_is_onepiece") === "true";
  });

  // 3. 하단 아이템 보관함 리스트
  const [storageItems, setStorageItems] = useState(() => {
    const saved = localStorage.getItem("wafit_storage_items");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse saved storage", e);
      }
    }
    return [
      {
        id: "default-model-item",
        slotKey: "image_1",
        slotLabel: "기본 모델",
        category: "model",
        imageUrl: DEFAULT_MODEL_IMAGE,
        name: "기본 모델 (스튜디오 컷)",
        createdAt: new Date().toISOString()
      }
    ];
  });

  // 4. 하단 보관함 탭 선택 (all, clothes, acc, bag_shoes, model)
  const [activeStorageTab, setActiveStorageTab] = useState("all");

  // 5. 좌측 과거 생성 룩북 히스토리
  const [historyList, setHistoryList] = useState(() => {
    const saved = localStorage.getItem("wafit_lookbook_history");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse saved history", e);
      }
    }
    return [];
  });

  // 6. 모달 관련 상태
  // (1) 슬롯 아이템 등록 모달
  const [showItemModal, setShowItemModal] = useState(false);
  const [activeSlotKey, setActiveSlotKey] = useState("image_6");
  const [itemInputTab, setItemInputTab] = useState("file"); // "file" | "url"
  const [inputUrl, setInputUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // (2) 룩북 고화질 뷰어 모달
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [selectedLook, setSelectedLook] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // 7. 생성(합성) 진행 상태 & 타이머
  const [isGenerating, setIsGenerating] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const timerRef = useRef(null);
  const tipTimerRef = useRef(null);

  // 로컬스토리지 동기화
  useEffect(() => {
    localStorage.setItem("wafit_equipped_slots", JSON.stringify(slots));
  }, [slots]);

  useEffect(() => {
    localStorage.setItem("wafit_is_onepiece", String(isOnepiece));
  }, [isOnepiece]);

  useEffect(() => {
    localStorage.setItem("wafit_storage_items", JSON.stringify(storageItems));
  }, [storageItems]);

  useEffect(() => {
    localStorage.setItem("wafit_lookbook_history", JSON.stringify(historyList));
  }, [historyList]);

  // 생성 중 타이머 및 팁 롤링
  useEffect(() => {
    if (isGenerating) {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);

      tipTimerRef.current = setInterval(() => {
        setTipIndex((prev) => (prev + 1) % LOADING_TIPS.length);
      }, 7000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      if (tipTimerRef.current) clearInterval(tipTimerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (tipTimerRef.current) clearInterval(tipTimerRef.current);
    };
  }, [isGenerating]);

  // 경과 시간 포맷 (MM:SS)
  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  // 장착된 부위 개수 계산 (image_1 제외 부위)
  const equippedCount = Object.keys(slots).filter(
    (key) => key !== "image_1" && slots[key]
  ).length;

  // 러블리 스타일 지수 계산 (게이미피케이션)
  const lovelyScore = Math.min(100, Math.round(20 + equippedCount * 8.8));

  // =========================================================================
  // 핸들러: 슬롯 클릭 -> 모달 오픈
  // =========================================================================
  const handleOpenSlotModal = (slotKey) => {
    setActiveSlotKey(slotKey);
    setInputUrl("");
    setUploadError("");
    setItemInputTab("file");
    setShowItemModal(true);
  };

  // 슬롯 아이템 해제/삭제
  const handleRemoveSlot = (e, slotKey) => {
    e.stopPropagation();
    if (slotKey === "image_1") {
      // 모델은 기본 모델로 복구
      setSlots((prev) => ({ ...prev, image_1: DEFAULT_MODEL_IMAGE }));
    } else {
      setSlots((prev) => ({ ...prev, [slotKey]: null }));
    }
  };

  // =========================================================================
  // 이미지 업로드 처리: n8n webhook/api/attachments 호출
  // =========================================================================
  const uploadImageFile = async (file, customName) => {
    const formData = new FormData();
    formData.append("attachment", file);

    const response = await axios.post(
      "https://n8n.wajok.com/webhook/api/attachments",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        timeout: 120000 // 2분
      }
    );

    if (
      response.data &&
      (response.data.status === "0000" || response.data.data?.fileName)
    ) {
      const uploadedUrl = response.data.data.fileName;
      const originalFileName =
        response.data.data.originalFileName || customName || file.name || "아이템";

      // 1. 현재 선택된 슬롯에 즉시 장착
      setSlots((prev) => ({ ...prev, [activeSlotKey]: uploadedUrl }));

      // 2. 하단 보관함에 새 아이템 추가
      const newItem = {
        id: "item-" + Date.now(),
        slotKey: activeSlotKey,
        slotLabel: SLOT_CONFIGS[activeSlotKey]?.label || "아이템",
        category: SLOT_CONFIGS[activeSlotKey]?.category || "clothes",
        imageUrl: uploadedUrl,
        name: originalFileName,
        createdAt: new Date().toISOString()
      };

      setStorageItems((prev) => [newItem, ...prev]);
      setShowItemModal(false);
      return uploadedUrl;
    } else {
      throw new Error(response.data?.message || "파일 업로드에 실패했습니다.");
    }
  };

  // 파일 입력 체인지
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setUploadError("");
      await uploadImageFile(file, file.name);
    } catch (err) {
      console.error("Upload error:", err);
      setUploadError(err.message || "파일 업로드 중 오류가 발생했습니다.");
    } finally {
      setUploading(false);
    }
  };

  // URL 입력 처리 (외부 URL도 attachments API로 변환)
  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    if (!inputUrl.trim()) {
      setUploadError("이미지 URL을 입력해주세요.");
      return;
    }

    const trimmedUrl = inputUrl.trim();

    // 이미 wajok 첨부파일 URL인 경우 바로 장착
    if (trimmedUrl.startsWith("https://www.wajok.com/attachments/")) {
      setSlots((prev) => ({ ...prev, [activeSlotKey]: trimmedUrl }));
      const newItem = {
        id: "item-" + Date.now(),
        slotKey: activeSlotKey,
        slotLabel: SLOT_CONFIGS[activeSlotKey]?.label || "아이템",
        category: SLOT_CONFIGS[activeSlotKey]?.category || "clothes",
        imageUrl: trimmedUrl,
        name: `${SLOT_CONFIGS[activeSlotKey]?.label} (URL)`,
        createdAt: new Date().toISOString()
      };
      setStorageItems((prev) => [newItem, ...prev]);
      setShowItemModal(false);
      return;
    }

    // 외부 URL일 경우: 클라이언트에서 fetch -> Blob 변환 후 n8n attachments 업로드 시도
    setUploading(true);
    setUploadError("");

    try {
      const res = await fetch(trimmedUrl, { mode: "cors" });
      if (!res.ok) throw new Error("이미지를 가져올 수 없습니다. (" + res.status + ")");
      const blob = await res.blob();
      const ext = blob.type.split("/")[1] || "png";
      const file = new File([blob], `web_image_${Date.now()}.${ext}`, { type: blob.type });

      await uploadImageFile(file, "웹 등록 아이템");
    } catch (err) {
      console.warn("Direct blob fetch failed, checking fallback:", err);
      setUploadError(
        "💡 외부 사이트 보안(CORS)으로 인해 링크 직접 수집이 제한되었습니다. 해당 이미지를 컴퓨터/휴대폰에 저장한 후 [파일 업로드] 탭에서 등록해주세요!"
      );
    } finally {
      setUploading(false);
    }
  };

  // =========================================================================
  // 하단 보관함 아이템 클릭 -> 상단 해당 슬롯 원클릭 장착
  // =========================================================================
  const handleEquipFromStorage = (item) => {
    setSlots((prev) => ({
      ...prev,
      [item.slotKey]: item.imageUrl
    }));
  };

  // 보관함 아이템 삭제
  const handleDeleteStorageItem = (e, itemId) => {
    e.stopPropagation();
    if (window.confirm("보관함에서 이 아이템을 삭제할까요?")) {
      setStorageItems((prev) => prev.filter((item) => item.id !== itemId));
    }
  };

  // 보관함 필터링
  const filteredStorageItems = storageItems.filter((item) => {
    if (activeStorageTab === "all") return true;
    if (activeStorageTab === "clothes") return item.category === "clothes";
    if (activeStorageTab === "acc") return item.category === "acc";
    if (activeStorageTab === "bag_shoes") return item.category === "bag_shoes";
    if (activeStorageTab === "model") return item.category === "model";
    return true;
  });

  // =========================================================================
  // AI 피팅 생성 (POST https://n8n.wajok.com/webhook/api/wafit)
  // =========================================================================
  const handleGenerateFit = async () => {
    // 필수 항목: image_1 (모델)
    const modelUrl = slots.image_1 || DEFAULT_MODEL_IMAGE;

    // 장착된 부위가 최소 1개 이상인지 확인
    if (equippedCount === 0) {
      if (!window.confirm("장착된 코디 아이템이 없습니다. 모델 원본으로 생성을 진행할까요?")) {
        return;
      }
    }

    // 전송할 페이로드 구성
    const payload = {
      image_1: modelUrl,
      is_onepiece: Boolean(isOnepiece)
    };

    if (slots.image_2) payload.image_2 = slots.image_2;
    if (slots.image_3) payload.image_3 = slots.image_3;
    if (slots.image_4) payload.image_4 = slots.image_4;
    if (slots.image_5) payload.image_5 = slots.image_5;
    if (slots.image_6) payload.image_6 = slots.image_6;
    if (slots.image_7) payload.image_7 = slots.image_7;
    if (slots.image_8) payload.image_8 = slots.image_8;
    if (slots.image_9) payload.image_9 = slots.image_9;
    if (slots.image_10) payload.image_10 = slots.image_10;

    setIsGenerating(true);

    try {
      // 10분 이상 타임아웃 대응 (15분 = 900,000ms)
      const response = await axios.post(
        "https://n8n.wajok.com/webhook/api/wafit",
        payload,
        {
          headers: {
            "Content-Type": "application/json"
          },
          timeout: 900000
        }
      );

      const resData = response.data;
      const finalImageUrl =
        resData.image_url ||
        resData.storage_response?.data?.fileName ||
        resData.data?.fileName;

      if (finalImageUrl) {
        // 새 히스토리 룩북 아이템 생성
        const newLook = {
          id: "look-" + Date.now(),
          imageUrl: finalImageUrl,
          promptId: resData.prompt_id || "wafit-" + Date.now(),
          date: new Date().toLocaleDateString("ko-KR", {
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
          }),
          equippedCount: equippedCount,
          isOnepiece: isOnepiece
        };

        setHistoryList((prev) => [newLook, ...prev]);

        // 결과 자동 확대 보기
        setSelectedLook(newLook);
        setShowViewerModal(true);
      } else {
        alert("생성은 완료되었으나 이미지 URL을 수신하지 못했습니다.");
      }
    } catch (err) {
      console.error("Wafit generation error:", err);
      alert(
        "AI 피팅 생성 중 오류가 발생했습니다.\n" +
          (err.response?.data?.message || err.message || "잠시 후 다시 시도해주세요.")
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // 룩북 이미지 다운로드
  const handleDownloadLook = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `wafit_fitting_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      // 직접 링크 열기 fallback
      window.open(imageUrl, "_blank");
    }
  };

  // 이미지 링크 복사
  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // 룩북 이미지 삭제
  const handleDeleteLook = (e, lookId) => {
    e.stopPropagation();
    if (window.confirm("이 피팅 룩을 마이 룩북에서 삭제할까요?")) {
      setHistoryList((prev) => prev.filter((item) => item.id !== lookId));
      if (selectedLook && selectedLook.id === lookId) {
        setShowViewerModal(false);
        setSelectedLook(null);
      }
    }
  };

  return (
    <div className="wafit-container">
      {/* 반짝임 배경 효과 */}
      <div className="wafit-bg-sparkles" />

      <Container fluid="lg" className="position-relative">
        {/* 상단 헤더 카드 */}
        {/* 상단 헤더 카드 (모던 시크 부티크 아틀리에) */}
        <div className="wafit-header-card">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
            <div>
              <div className="wafit-header-badge">GLAMOUR COUTURE • AI DRESSING ROOM</div>
              <h1 className="wafit-title">
                와핏 아틀리에 <span className="wafit-title-sub">WAFIT ATELIER</span>
              </h1>
              <p className="wafit-subtitle">
                선반 위의 감각적인 의류와 악세사리를 선택하여, 아치 미러 속 모델에게 프라이빗 피팅을 완성해보세요 ✨
              </p>
            </div>

            {/* 헤더 우측 컨트롤 (와핏! 생성 버튼) */}
            <div className="wafit-header-controls">
              <button
                type="button"
                className="wafit-generate-btn"
                onClick={handleGenerateFit}
                disabled={isGenerating}
              >
                <FaMagic /> <span>TRY ON • 와핏 피팅</span>
              </button>
            </div>
          </div>
        </div>

        {/* 메인 2단/3단 레이아웃 (좌측 OOTD 룩북 & 스탯 / 우측 인벤토리 피팅룸) */}
        <div className="wafit-main-grid">
          {/* ================================================================= */}
          {/* [좌측 영역] OOTD 스타일 룸 & 과거 생성 룩북 (디아블로4 스탯/재료창) */}
          {/* ================================================================= */}
          <div className="wafit-sidebar-card">
            {/* 게이미피케이션 스탯 박스 */}
            <div>
              <div className="wafit-section-title mb-2">
                <span>🎀 스타일링 스탯</span>
                <span className="badge-cnt">{equippedCount} / 9 부위</span>
              </div>
              <div className="wafit-stats-box">
                <div className="wafit-stat-row">
                  <span className="wafit-stat-label">
                    <IoSparkles style={{ color: "#f43f6e" }} /> 러블리 지수
                  </span>
                  <span className="wafit-stat-val">{lovelyScore} %</span>
                </div>
                <div className="wafit-stat-row">
                  <span className="wafit-stat-label">
                    <IoShirtOutline style={{ color: "#a855f7" }} /> 코디 모드
                  </span>
                  <span className="wafit-stat-val" style={{ color: "#7e22ce" }}>
                    {isOnepiece ? "원피스 룩 👗" : "투피스 룩 👚"}
                  </span>
                </div>
                <div className="wafit-stat-row">
                  <span className="wafit-stat-label">
                    <FaHistory style={{ color: "#3b82f6" }} /> 룩북 피팅 횟수
                  </span>
                  <span className="wafit-stat-val" style={{ color: "#2563eb" }}>
                    {historyList.length} 회
                  </span>
                </div>
              </div>
            </div>

            {/* 과거 피팅 룩북 히스토리 리스트 */}
            <div className="flex-grow-1 d-flex flex-column w-100" style={{ minHeight: 0 }}>
              <div className="wafit-section-title mb-2">
                <span>👗 마이 룩북</span>
                <span className="badge-cnt">{historyList.length}건</span>
              </div>

              <div className="wafit-history-list">
                {historyList.length === 0 ? (
                  <div className="wafit-history-empty">
                    <GiSparkles style={{ fontSize: "2rem", color: "#fda4b4", marginBottom: "8px" }} />
                    <br />
                    아직 생성된 피팅 룩이 없어요.
                    <br />
                    상단 <strong>와핏!</strong> 버튼을 눌러보세요!
                  </div>
                ) : (
                  historyList.map((look) => (
                    <motion.div
                      key={look.id}
                      className="wafit-history-item"
                      whileHover={{ scale: 1.02 }}
                      onClick={() => {
                        setSelectedLook(look);
                        setShowViewerModal(true);
                      }}
                    >
                      <img src={look.imageUrl} alt="생성 룩" loading="lazy" />
                      <button
                        type="button"
                        className="wafit-history-item-del-btn"
                        onClick={(e) => handleDeleteLook(e, look.id)}
                        title="룩북에서 삭제"
                      >
                        <FaTimes />
                      </button>
                      <div className="wafit-history-item-overlay">
                        <span>자세히 보기 🔍</span>
                        {look.date && (
                          <span className="wafit-history-item-date">{look.date}</span>
                        )}
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* ================================================================= */}
          {/* ================================================================= */}
          {/* [중앙 피팅룸] 아치형 LED 미러 & 양옆 플로팅 선반 쇼룸 */}
          {/* ================================================================= */}
          <div className="wafit-dressing-room-card">
            <div className="wafit-stage-header mb-3">
              <div>
                <div className="wafit-stage-eyebrow">SHOWROOM MIRROR & FLOATING SHELVES</div>
                <h2 className="wafit-section-title mb-0">
                  <span>🪞 부티크 아치 미러 & 플로팅 선반</span>
                </h2>
              </div>
              <small className="wafit-stage-hint">
                각 선반의 슬롯을 터치하여 코디 아이템을 등록해보세요
              </small>
            </div>

            <div className="wafit-inventory-stage">
              {/* 좌측 플로팅 선반 컬럼 (머리, 눈, 귀, 목, 상의) */}
              <div className="wafit-slots-col">
                {LEFT_SLOT_KEYS.map((key) => {
                  const cfg = SLOT_CONFIGS[key];
                  const isEquipped = Boolean(slots[key]);
                  return (
                    <div key={key} className="wafit-shelf-slot-wrapper">
                      <div className="wafit-shelf-tag">{cfg.enLabel}</div>
                      <div
                        className={`wafit-slot-box ${isEquipped ? "is-equipped" : ""}`}
                        onClick={() => handleOpenSlotModal(key)}
                        title={`${cfg.label} 등록`}
                      >
                        {isEquipped ? (
                          <>
                            <img src={slots[key]} alt={cfg.label} className="wafit-slot-img" />
                            <button
                              type="button"
                              className="wafit-slot-delete-btn"
                              onClick={(e) => handleRemoveSlot(e, key)}
                              title="장착 해제"
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="wafit-slot-icon">{cfg.icon}</div>
                            <span className="wafit-slot-label">{cfg.label}</span>
                          </>
                        )}
                      </div>
                      <div className="wafit-floating-shelf-bar" />

                      {/* 상의 슬롯 하단에 원피스 코디 미니 토글 배치 */}
                      {key === "image_6" && (
                        <div
                          className={`wafit-mini-onepiece-toggle ${isOnepiece ? "active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsOnepiece(!isOnepiece);
                          }}
                          title="원피스 코디 모드 ON/OFF"
                        >
                          <GiRibbon style={{ color: isOnepiece ? "#b8826e" : "#8c7379", fontSize: "0.85rem" }} />
                          <span>
                            원피스 {isOnepiece ? "ON" : "OFF"}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* 중앙 모델 전신 아치 미러 스테이지 */}
              <div className="wafit-model-center-stage">
                <div className="wafit-arch-mirror-frame">
                  {/* 앰비언트 LED 후광 조명 */}
                  <div className="wafit-arch-led-halo" />
                  
                  <div
                    className="wafit-arch-mirror-glass"
                    onClick={() => handleOpenSlotModal("image_1")}
                    title="모델 사진 변경하기"
                  >
                    {/* 사용자 가변 이미지 비율 대응: 배경 앰비언트 블러 레이어 */}
                    <img
                      src={slots.image_1 || DEFAULT_MODEL_IMAGE}
                      alt="앰비언트 조명"
                      className="wafit-mirror-ambient-bg"
                    />
                    {/* 전면 피팅 모델 (contain으로 머리/발끝 잘림 없이 100% 보존) */}
                    <img
                      src={slots.image_1 || DEFAULT_MODEL_IMAGE}
                      alt="피팅 모델"
                      className="wafit-model-img"
                    />
                    <div className="wafit-model-change-btn">
                      <GiMirrorMirror /> 모델 사진 교체
                    </div>
                  </div>
                </div>
              </div>

              {/* 우측 플로팅 선반 컬럼 (가방, 하의, 스타킹, 신발) */}
              <div className="wafit-slots-col">
                {RIGHT_SLOT_KEYS.map((key) => {
                  const cfg = SLOT_CONFIGS[key];
                  const isEquipped = Boolean(slots[key]);
                  return (
                    <div key={key} className="wafit-shelf-slot-wrapper">
                      <div className="wafit-shelf-tag">{cfg.enLabel}</div>
                      <div
                        className={`wafit-slot-box ${isEquipped ? "is-equipped" : ""}`}
                        onClick={() => handleOpenSlotModal(key)}
                        title={`${cfg.label} 등록`}
                      >
                        {isEquipped ? (
                          <>
                            <img src={slots[key]} alt={cfg.label} className="wafit-slot-img" />
                            <button
                              type="button"
                              className="wafit-slot-delete-btn"
                              onClick={(e) => handleRemoveSlot(e, key)}
                              title="장착 해제"
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="wafit-slot-icon">{cfg.icon}</div>
                            <span className="wafit-slot-label">{cfg.label}</span>
                          </>
                        )}
                      </div>
                      <div className="wafit-floating-shelf-bar" />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* =================================================================== */}
        {/* [하단 영역] 마이 드레스룸 벨벳 쇼케이스 (아이템 보관함 창) */}
        {/* =================================================================== */}
        <div className="wafit-storage-card">
          <div className="wafit-storage-header">
            <div>
              <div className="wafit-stage-eyebrow">BOUTIQUE JEWELRY & FASHION TRAY</div>
              <h2 className="wafit-section-title mb-1">
                <span>🧰 마이 드레스룸 쇼케이스 서랍</span>
              </h2>
              <p className="mb-0 text-muted small">
                쇼케이스 트레이의 아이템을 터치하면 아치 미러에 즉시 장착됩니다.
              </p>
            </div>

            {/* 카테고리 탭 (전체, 의류, 악세사리, 가방/신발, 모델) */}
            <div className="wafit-tabs-wrapper">
              <button
                type="button"
                className={`wafit-tab-btn ${activeStorageTab === "all" ? "active" : ""}`}
                onClick={() => setActiveStorageTab("all")}
              >
                전체 ({storageItems.length})
              </button>
              <button
                type="button"
                className={`wafit-tab-btn ${activeStorageTab === "clothes" ? "active" : ""}`}
                onClick={() => setActiveStorageTab("clothes")}
              >
                👗 의류
              </button>
              <button
                type="button"
                className={`wafit-tab-btn ${activeStorageTab === "acc" ? "active" : ""}`}
                onClick={() => setActiveStorageTab("acc")}
              >
                💍 악세사리
              </button>
              <button
                type="button"
                className={`wafit-tab-btn ${activeStorageTab === "bag_shoes" ? "active" : ""}`}
                onClick={() => setActiveStorageTab("bag_shoes")}
              >
                👠 가방·신발
              </button>
              <button
                type="button"
                className={`wafit-tab-btn ${activeStorageTab === "model" ? "active" : ""}`}
                onClick={() => setActiveStorageTab("model")}
              >
                👤 모델
              </button>
            </div>
          </div>

          {/* 보관함 타일 그리드 */}
          <div className="wafit-storage-grid">
            {filteredStorageItems.length === 0 ? (
              <div className="wafit-storage-empty">
                보관함에 등록된 아이템이 없습니다.
                <br />
                상단 슬롯을 눌러 예쁜 옷이나 악세사리를 업로드해보세요!
              </div>
            ) : (
              filteredStorageItems.map((item) => {
                const isEquippedNow = slots[item.slotKey] === item.imageUrl;
                return (
                  <motion.div
                    key={item.id}
                    className={`wafit-storage-item ${isEquippedNow ? "is-currently-equipped" : ""}`}
                    whileHover={{ scale: 1.05 }}
                    onClick={() => handleEquipFromStorage(item)}
                    title={`${item.name} 장착하기`}
                  >
                    <img src={item.imageUrl} alt={item.name} loading="lazy" />
                    <div className="wafit-storage-item-label">{item.slotLabel}</div>
                    {item.id !== "default-model-item" && (
                      <button
                        type="button"
                        className="wafit-storage-item-del"
                        onClick={(e) => handleDeleteStorageItem(e, item.id)}
                        title="보관함에서 삭제"
                      >
                        ✕
                      </button>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </Container>

      {/* =================================================================== */}
      {/* [모달 1] 아이템 등록 / 업로드 모달 */}
      {/* =================================================================== */}
      <Modal
        show={showItemModal}
        onHide={() => !uploading && setShowItemModal(false)}
        centered
        className="wafit-modal"
      >
        <Modal.Header closeButton={!uploading} className="wafit-modal-header">
          <Modal.Title className="wafit-modal-title">
            <span style={{ color: "#f43f6e" }}>{SLOT_CONFIGS[activeSlotKey]?.icon}</span>
            <span>{SLOT_CONFIGS[activeSlotKey]?.label} 등록</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="wafit-modal-body">
          <p className="text-muted small mb-3">
            {SLOT_CONFIGS[activeSlotKey]?.desc}
            <br />
            컴퓨터나 스마트폰의 사진을 올리거나 이미지 링크(URL)를 입력해주세요.
          </p>

          {/* 탭 전환 (파일 업로드 vs URL 입력) */}
          <div className="wafit-tabs-wrapper mb-3">
            <button
              type="button"
              className={`wafit-tab-btn flex-grow-1 justify-content-center ${
                itemInputTab === "file" ? "active" : ""
              }`}
              onClick={() => setItemInputTab("file")}
              disabled={uploading}
            >
              📁 파일 직접 업로드
            </button>
            <button
              type="button"
              className={`wafit-tab-btn flex-grow-1 justify-content-center ${
                itemInputTab === "url" ? "active" : ""
              }`}
              onClick={() => setItemInputTab("url")}
              disabled={uploading}
            >
              🔗 이미지 URL 링크
            </button>
          </div>

          {uploadError && (
            <Alert variant="danger" className="py-2 small rounded-3">
              {uploadError}
            </Alert>
          )}

          {itemInputTab === "file" ? (
            <div>
              <label htmlFor="wafit-file-input" className="w-100" style={{ cursor: "pointer" }}>
                <div className="wafit-dropzone">
                  <div className="wafit-dropzone-icon">
                    {uploading ? <Spinner animation="border" variant="danger" /> : <FaPlus />}
                  </div>
                  <div className="wafit-dropzone-txt">
                    {uploading ? "서버에 안전하게 업로드 중..." : "이미지 파일 선택 또는 끌어놓기"}
                  </div>
                  <div className="wafit-dropzone-sub">JPG, PNG, WEBP 등 지원</div>
                </div>
              </label>
              <input
                id="wafit-file-input"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
                disabled={uploading}
              />
            </div>
          ) : (
            <Form onSubmit={handleUrlSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-secondary">
                  웹 이미지 주소 (URL)
                </Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={inputUrl}
                  onChange={(e) => setInputUrl(e.target.value)}
                  disabled={uploading}
                  className="rounded-3"
                />
              </Form.Group>
              <Button
                type="submit"
                variant="danger"
                className="w-100 rounded-3 py-2 fw-bold"
                disabled={uploading || !inputUrl.trim()}
                style={{ background: "linear-gradient(135deg, #f43f6e, #db2777)", border: "none" }}
              >
                {uploading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    가져오는 중...
                  </>
                ) : (
                  "아이템 등록 & 장착하기"
                )}
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* =================================================================== */}
      {/* [모달 2] 룩북 고화질 뷰어 & 다운로드 모달 */}
      {/* =================================================================== */}
      <Modal
        show={showViewerModal}
        onHide={() => setShowViewerModal(false)}
        centered
        size="lg"
        className="wafit-modal"
      >
        <Modal.Header closeButton className="wafit-modal-header">
          <Modal.Title className="wafit-modal-title">
            <GiSparkles style={{ color: "#f43f6e" }} />
            <span>나만의 피팅 완성 룩북</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="wafit-modal-body p-4 text-center">
          {selectedLook && (
            <div>
              <div
                style={{
                  maxHeight: "68vh",
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 12px 36px rgba(0,0,0,0.12)",
                  background: "#f8fafc",
                  display: "inline-block",
                  position: "relative"
                }}
              >
                <img
                  src={selectedLook.imageUrl}
                  alt="완성 룩"
                  style={{
                    maxHeight: "65vh",
                    maxWidth: "100%",
                    objectFit: "contain",
                    display: "block"
                  }}
                />
              </div>

              <div className="mt-3 text-muted small">
                생성 일시: {selectedLook.date}
              </div>

              <div className="d-flex justify-content-center gap-2 mt-3 flex-wrap">
                <Button
                  variant="danger"
                  className="rounded-pill px-4 fw-bold shadow-sm"
                  style={{ background: "linear-gradient(135deg, #f43f6e, #db2777)", border: "none" }}
                  onClick={() => handleDownloadLook(selectedLook.imageUrl)}
                >
                  <FaDownload className="me-2" /> 이미지 다운로드
                </Button>
                <Button
                  variant="outline-secondary"
                  className="rounded-pill px-3"
                  onClick={() => handleCopyUrl(selectedLook.imageUrl)}
                >
                  {copySuccess ? (
                    <>
                      <FaCheck className="me-1 text-success" /> 복사 완료!
                    </>
                  ) : (
                    <>
                      <FaRegCopy className="me-1" /> 이미지 URL 복사
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-danger"
                  className="rounded-pill px-3"
                  onClick={(e) => handleDeleteLook(e, selectedLook.id)}
                >
                  <FaTimes className="me-1" /> 삭제
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* =================================================================== */}
      {/* [오버레이] 생성 진행 중 대기 화면 (최대 10분 소요 대응) */}
      {/* =================================================================== */}
      {isGenerating && (
        <div className="wafit-loading-overlay">
          <div className="wafit-loading-card">
            <div className="wafit-loading-magic-icon">
              <GiSparkles />
            </div>
            <h3 className="wafit-loading-title">AI 피팅이 진행 중이에요</h3>
            <p className="wafit-loading-desc">
              선택하신 옷과 악세사리를 모델에 자연스럽게 입히고 있어요.
              <br />
              고해상도 합성을 위해 최대 5~10분이 소요될 수 있습니다.
            </p>

            <div className="wafit-timer-badge">
              <Spinner size="sm" animation="border" variant="danger" />
              <span>진행 시간: {formatTime(elapsedSeconds)}</span>
            </div>

            <div className="wafit-tip-box">
              <AnimatePresence mode="wait">
                <motion.div
                  key={tipIndex}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }}
                >
                  {LOADING_TIPS[tipIndex]}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
